import { useEffect, useState, useCallback } from "react";
import { PenLine, FileSignature, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SignActions } from "@/components/SignActions";
import { nb, nok } from "@/lib/format";

function SignTaskDialog({ task, onOpenChange, onDone }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  useEffect(() => {
    setPdfUrl(null);
    if (!task) return;
    const base = task.entity_type === "settlement" ? `/settlements/${task.entity_id}` : `/agreements/${task.entity_id}`;
    api.get(`${base}/pdf-link`).then((r) => setPdfUrl(r.data.url)).catch(() => setPdfUrl(null));
  }, [task]);

  return (
    <Dialog open={!!task} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sign-task-dialog">
        <DialogHeader>
          <DialogTitle>Signer {task?.entity_title}</DialogTitle>
          <DialogDescription>
            {task?.on_behalf_company
              ? <>Du signerer på vegne av <b>{task?.on_behalf_company}</b> ({task?.party_role}).</>
              : <>Som styreleder i <b>{task?.target_company_name}</b> signerer du på vegne av selskapet ({task?.party_role}).</>}
          </DialogDescription>
        </DialogHeader>
        {task ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-sm">
              <p><span className="text-muted-foreground">Selskap (utsteder):</span> {task.issuer_company_name}</p>
              {task.shares ? (
                <p className="mt-1"><span className="text-muted-foreground">Handel:</span> {nb(task.shares)} {task.share_class}{task.price_per_share ? ` · ${nok(task.price_per_share)}/aksje` : ""}</p>
              ) : null}
            </div>
            {pdfUrl ? (
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="sign-task-pdf">
                <FileText className="h-4 w-4" /> Se dokumentet (PDF)
              </a>
            ) : null}
            <div className="flex flex-wrap gap-2 pt-1">
              <SignActions entityType={task.entity_type} entityId={task.entity_id} onChanged={onDone} />
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function SigningTasks() {
  const [tasks, setTasks] = useState(null);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    api.get("/me/signing-tasks").then((r) => setTasks(r.data)).catch(() => setTasks([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!tasks || tasks.length === 0) return null;

  return (
    <section className="mt-10" data-testid="signing-tasks">
      <div className="flex items-center gap-2">
        <FileSignature className="h-5 w-5 text-primary" />
        <h2 className="font-headings text-lg font-bold tracking-tight">Venter på din signatur</h2>
        <span className="rounded-full bg-lime px-2 py-0.5 text-xs font-bold text-lime-foreground">{tasks.length}</span>
      </div>
      <div className="mt-4 space-y-3">
        {tasks.map((t) => (
          <div key={t.id} data-testid={`signtask-${t.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-primary/25 bg-primary/[0.04] p-4 shadow-sm">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
              <FileSignature className="h-5 w-5" strokeWidth={1.7} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium">{t.entity_title} · {t.issuer_company_name}</p>
              <p className="text-xs text-muted-foreground">
                {t.on_behalf_company
                  ? <>Du signerer <b>på vegne av {t.on_behalf_company}</b></>
                  : <>Du signerer som <b>{t.party_role}</b> for {t.target_company_name}</>}
                {t.shares ? ` · ${nb(t.shares)} ${t.share_class}` : ""}
              </p>
            </div>
            <Button className="gap-1.5" data-testid={`signtask-open-${t.id}`} onClick={() => setActive(t)}>
              <PenLine className="h-4 w-4" /> Les og signer
            </Button>
          </div>
        ))}
      </div>
      <SignTaskDialog task={active} onOpenChange={(o) => !o && setActive(null)} onDone={() => { setActive(null); load(); }} />
    </section>
  );
}
